import type { ISODateTime } from './ids';

/**
 * **OUR OWN SIGN-IN ON BOTH SITES** (Evan, 2026-09-26; board O00, O00b, O00c, O00d). No Auth0 page, name or email on
 * screen: the portal asks for an email first, and the API decides the way in.
 *
 * - A company address (`COMPANY_EMAIL_DOMAINS`) goes straight to Google, limited to those two domains. A personal
 *   account picked there by mistake is refused with `notCompanyAccount` ("Use your Kredit Academy account.").
 * - Any other address is mailed a six-digit code (O00d) — **whatever the address**, so the page never shows who has
 *   access. The account code rules hold (15 minutes, five wrong tries void it, a new code after 60 seconds), with a
 *   limit per address and per device on top (`STAFF_CODES_PER_HOUR_*`).
 * - Either way the answer is a Kiddie Kredit session: a one-hour access token and a refresh token that works on the
 *   same computer for `STAFF_SESSION_DAYS` from the sign-in. Publishing asks for a sign-in inside the last
 *   `STAFF_PUBLISH_SIGN_IN_HOURS` (FORBIDDEN, `details.reason: 'signInAgain'`).
 *
 * A session opens the portal's routes only (`/v1/org`, `/v1/admin`, `/v1/me/email`, `/v1/auth/staff`): it is the
 * same person as the account the address resolves to, and a family's routes stay with the app's own sign-in.
 */

/** Addresses that sign in with Google on both sites, and the only ones that can be Owner or Publisher. */
export const COMPANY_EMAIL_DOMAINS = ['kreditacademy.com', 'kiddiekredit.com'] as const;

/** Whether `email` is a Kredit Academy company address (case and surrounding space ignored). */
export function isCompanyEmail(email: string): boolean {
  const domain = email.trim().toLowerCase().split('@')[1] ?? '';
  return (COMPANY_EMAIL_DOMAINS as readonly string[]).includes(domain);
}

/** "Sessions last 30 days on the same computer" — counted from the sign-in, not slid by use. */
export const STAFF_SESSION_DAYS = 30;
/** "Publishing asks you to sign in again if it's been more than 12 hours." */
export const STAFF_PUBLISH_SIGN_IN_HOURS = 12;
/** How long one access token works; the portal refreshes it quietly inside the session. */
export const STAFF_ACCESS_TOKEN_MINUTES = 60;
/** Codes mailed to one address in an hour; past it RATE_LIMITED `tooManyCodes`, whoever the address belongs to. */
export const STAFF_CODES_PER_HOUR_PER_ADDRESS = 5;
/** Codes one device may have mailed in an hour, across every address it typed. */
export const STAFF_CODES_PER_HOUR_PER_DEVICE = 10;

/** Which site is asking: the clubs portal or the admin. */
export type StaffSite = 'clubs' | 'admin';

/**
 * POST /v1/auth/staff/start (O00 / O00c "Continue"). Unauthenticated. `deviceId` is the browser's own random id,
 * kept in its storage: the per-device limit counts it, and a session is bound to it.
 */
export interface StaffSignInStartInput {
  email: string;
  site: StaffSite;
  deviceId: string;
}

/**
 * `google` for a company address (nothing is mailed); `code` for EVERY other address, with the same keys whether or
 * not it has access to anything. `requestedAt` is the server's moment, for the 60 seconds on O00d.
 */
export type StaffSignInStart = { method: 'google' } | { method: 'code'; requestedAt: ISODateTime };

/**
 * POST /v1/auth/staff/code (O00d). Unauthenticated; the code is the authority. Refusals: VALIDATION ·
 * INVALID_STATE (no live code; `codeExpired` when its 15 minutes are up) · INVALID_CREDENTIALS (`attemptsLeft`) ·
 * RATE_LIMITED `codeVoided` (five wrong tries).
 */
export interface StaffSignInCodeInput {
  email: string;
  code: string;
  /** Where the sign-in happens: an admin sign-in goes in the content audit log. */
  site: StaffSite;
  deviceId: string;
}

/**
 * POST /v1/auth/staff/google: the Google sign-in's token (the bearer) becomes a Kiddie Kredit session.
 * `companyOnly` is set when the email-first step chose Google for a company address; the admin always sets it.
 * FORBIDDEN `notCompanyAccount` for any other account ("Use your Kredit Academy account.").
 */
export interface StaffGoogleSignInInput {
  site: StaffSite;
  deviceId: string;
  companyOnly: boolean;
}

/** POST /v1/auth/staff/refresh: a new access token inside the session, on the device it was signed in on. */
export interface StaffRefreshInput {
  refreshToken: string;
  deviceId: string;
}

/** POST /v1/auth/staff/sign-out: the session ends at once, its access token with it. */
export interface StaffSignOutInput {
  refreshToken: string;
}

/**
 * A Kiddie Kredit staff session. The refresh token is new on every refresh (the one before stops working);
 * `expiresAt` is the session's end, 30 days from `signedInAt`, which no refresh moves.
 */
export interface StaffSignedIn {
  accessToken: string;
  accessExpiresAt: ISODateTime;
  refreshToken: string;
  expiresAt: ISODateTime;
  signedInAt: ISODateTime;
  email: string;
  method: 'code' | 'google';
}

/** `details.reason` on the sign-in's refusals the screens word differently. */
export type StaffSignInRefusal = 'notCompanyAccount' | 'tooManyCodes' | 'signInAgain';
